/**
 * 手机选点上图 / 清理（从 Dashboard 拆出）
 * 业务坐标为 WGS84；高德/谷歌底图需 toMapLngLat → GCJ-02
 * 取样点三色圆点 + 橙色自动最终点 + 蓝色手工点 + 连线距离
 */
import mapboxgl from 'mapbox-gl';
import {
  createPickPointElement,
  createPickSampleElement,
  createPickDistanceLabel,
  buildPickPointPopupHtml
} from './rescueMarkers';
import { toMapLngLat } from './basemapCoord';
import {
  normalizeSamples,
  resolveAutoPoint,
  resolveManualPoint,
  haversineMeters,
  formatStraightDistance,
  hasMeaningfulManualPoint,
  pickPointMidpoint,
  PICK_AUTO_COLOR,
  PICK_MANUAL_COLOR
} from '../pickPointCapture';

function readProjectionName(map) {
  try {
    return map?.getProjection?.()?.name || 'mercator';
  } catch (_) {
    return 'mercator';
  }
}
function pickPointLineIds(id) {
  return {
    source: `zhxt-pick-line-${id}`,
    layer: `zhxt-pick-line-${id}`
  };
}

function isPickPointBundle(entry) {
  return !!(entry && (entry.auto || entry.manual || Array.isArray(entry.samples) || entry.lineIds));
}

function removeMapLine(map, ids) {
  if (!map || !ids) return;
  try {
    if (map.getLayer?.(ids.layer)) map.removeLayer(ids.layer);
  } catch (_) { /* ignore */ }
  try {
    if (map.getSource?.(ids.source)) map.removeSource(ids.source);
  } catch (_) { /* ignore */ }
}

function disposePickPointBundle(bundle) {
  if (!bundle) return;
  try { bundle.auto?.remove(); } catch (_) { /* ignore */ }
  (bundle.samples || []).forEach((marker) => {
    try { marker.remove(); } catch (_) { /* ignore */ }
  });
  try { bundle.manual?.remove(); } catch (_) { /* ignore */ }
  try { bundle.lineMarker?.remove(); } catch (_) { /* ignore */ }
  removeMapLine(bundle.map, bundle.lineIds);
}

export function canDisplayPickPoint(point) {
  if (!point) return false;
  if (resolveAutoPoint(point)) return true;
  if (resolveManualPoint(point)) return true;
  return normalizeSamples(point.capture_samples).length > 0;
}

function bindPickPointPopupDelete(popup, point, onDelete) {
  if (!popup || !onDelete || point?.id == null) return;
  const root = popup.getElement?.();
  if (!root) return;
  const btn = root.querySelector(`[data-zhxt-pick-delete="${String(point.id)}"]`);
  if (!btn || btn.__zhxtBound) return;
  btn.__zhxtBound = true;
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(point);
  });
}

function addOrUpdateLine(map, ids, fromDisplay, toDisplay) {
  const geojson = {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: [fromDisplay, toDisplay]
    }
  };
  try {
    const source = map.getSource?.(ids.source);
    if (source) {
      source.setData(geojson);
      return;
    }
    map.addSource(ids.source, { type: 'geojson', data: geojson });
    map.addLayer({
      id: ids.layer,
      type: 'line',
      source: ids.source,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': '#0f172a',
        'line-width': 2.5,
        'line-dasharray': [2, 1.4],
        'line-opacity': 0.85
      }
    });
  } catch (_) {
    /* 底图切换瞬间可能没有 style */
  }
}

/**
 * 上图或按当前底图纠偏更新已有选点标记
 * @param {string} [basemapId] 显式底图 id（切图纠偏时务必传入）
 * @param {{ onDelete?: (point) => void, canDelete?: boolean }} [options]
 */
export function displayPickPointOnMap(map, point, markersMap, basemapId, options = {}) {
  if (!map || !canDisplayPickPoint(point)) return null;
const projection = readProjectionName(map);
  if (projection === 'globe') {
    return null;
  }

  const id = String(point.id ?? point.token ?? '');
  if (!id) return null;

  if (markersMap?.has?.(id)) {
    disposePickPointBundle(markersMap.get(id));
    markersMap.delete(id);
  }

  const canDelete = options.canDelete === true && typeof options.onDelete === 'function';
  const autoPoint = resolveAutoPoint(point);
  const manualPoint = hasMeaningfulManualPoint(autoPoint, resolveManualPoint(point))
    ? resolveManualPoint(point)
    : null;
  const samples = point.status === 'used'
    ? []
    : normalizeSamples(point.capture_samples);
  const distanceMeters = autoPoint && manualPoint ? haversineMeters(autoPoint, manualPoint) : null;
  const distanceText = formatStraightDistance(distanceMeters);
  const extraHtml = distanceText
    ? `<div style="margin-top:4px;font-weight:700">${distanceText}</div>`
    : '';
  const popupOptions = { canDelete, extraHtml };
  const makePopup = () => {
    const popup = new mapboxgl.Popup({ offset: 16, closeButton: true })
      .setHTML(buildPickPointPopupHtml(point, popupOptions));
    if (canDelete) {
      popup.on('open', () => {
        bindPickPointPopupDelete(popup, point, options.onDelete);
      });
    }
    return popup;
  };

  const sampleMarkers = samples.map((sample) => {
    const display = toMapLngLat(sample.lng, sample.lat, basemapId, map);
    if (!display) return null;
    const marker = new mapboxgl.Marker({ element: createPickSampleElement(sample.color, sample.seq), anchor: 'center' })
      .setLngLat(display)
      .setPopup(makePopup())
      .addTo(map);
    return marker;
  }).filter(Boolean);

  let autoMarker = null;
  if (autoPoint) {
    const display = toMapLngLat(autoPoint.lng, autoPoint.lat, basemapId, map);
    if (display) {
      // 如果有自定义图标，使用自定义图标；否则使用默认橙色水滴
      const iconUrl = point.icon_url ? point.icon_url : null;
      autoMarker = new mapboxgl.Marker({
        element: createPickPointElement(PICK_AUTO_COLOR, '自动获取坐标', iconUrl),
        anchor: iconUrl ? 'center' : 'bottom'
      })
        .setLngLat(display)
        .setPopup(makePopup())
        .addTo(map);
    }
  }

  let manualMarker = null;
  if (manualPoint) {
    const display = toMapLngLat(manualPoint.lng, manualPoint.lat, basemapId, map);
    if (display) {
      manualMarker = new mapboxgl.Marker({
        element: createPickPointElement(PICK_MANUAL_COLOR, '手工修改坐标'),
        anchor: 'bottom'
      })
        .setLngLat(display)
        .setPopup(makePopup())
        .addTo(map);
    }
  }

  const lineIds = pickPointLineIds(id);
  let lineMarker = null;
  if (autoPoint && manualPoint && Number.isFinite(distanceMeters) && distanceMeters >= 1) {
    const fromDisplay = toMapLngLat(autoPoint.lng, autoPoint.lat, basemapId, map);
    const toDisplay = toMapLngLat(manualPoint.lng, manualPoint.lat, basemapId, map);
    if (fromDisplay && toDisplay) {
      addOrUpdateLine(map, lineIds, fromDisplay, toDisplay);
      const mid = pickPointMidpoint(
        { lng: fromDisplay[0], lat: fromDisplay[1] },
        { lng: toDisplay[0], lat: toDisplay[1] }
      );
      if (mid && distanceText) {
        lineMarker = new mapboxgl.Marker({ element: createPickDistanceLabel(distanceText), anchor: 'center' })
          .setLngLat([mid.lng, mid.lat])
          .addTo(map);
      }
    }
  }

  const bundle = {
    auto: autoMarker,
    samples: sampleMarkers,
    manual: manualMarker,
    lineMarker,
    lineIds,
    map
  };
  if (markersMap?.set) markersMap.set(id, bundle);
  return bundle;
}

/**
 * 按当前底图批量纠偏已上图的选点（不重新请求接口）
 */
export function reprojectPickPointMarkers(map, points = [], markersMap, basemapId, options = {}) {
  if (!map || !markersMap) return;
  (points || []).forEach((point) => {
    if (!canDisplayPickPoint(point)) return;
    displayPickPointOnMap(map, point, markersMap, basemapId, options);
  });
}

export function removePickPointFromMap(markersMap, id) {
  if (!markersMap) return;
  const key = String(id);
  const entry = markersMap.get(key);
  if (isPickPointBundle(entry)) {
    disposePickPointBundle(entry);
  } else if (entry && typeof entry.remove === 'function') {
    entry.remove();
  }
  markersMap.delete(key);
}

export function clearAllPickPointMarkers(markersMap) {
  if (!markersMap) return;
  markersMap.forEach((entry) => {
    if (isPickPointBundle(entry)) {
      disposePickPointBundle(entry);
      return;
    }
    try { entry?.remove?.(); } catch (_) { /* ignore */ }
  });
  markersMap.clear();
}

/**
 * @param {(url: string) => Promise<Response>} fetchFn
 * @returns {Promise<Array>}
 */
export async function fetchPickPointsForMap(fetchFn) {
  const response = await fetchFn('/api/pick-point/list?for_map=1');
  const data = await response.json();
  if (!response.ok || !data.success) {
    throw new Error(data.error || '加载手机选点失败');
  }
  return data.tokens || [];
}

/** 删除已上图的手机选点（软删） */
export async function deletePickPointById(fetchFn, id) {
  const response = await fetchFn(`/api/pick-point/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || '删除选点失败');
  }
  return data.point || null;
}
